"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { Z_INDEX } from "components/ui/v2/z-index";
import { REACTION_EMOJI } from "utils/reactionEmoji";

const GIANT_REACTION_HOLD_MS = 920;
const GIANT_REACTION_EXIT_MS = 560;
const PARTICLE_BASE_DURATION_MS = 740;
const PARTICLE_EASE = "cubic-bezier(0.34, 1.56, 0.64, 1)";

type GiantReactionParticleTemplate = {
  x: number;
  y: number;
  rotation: number;
  delay: number;
  scale: number;
  speedFactor: number;
};

type GiantReactionParticle = GiantReactionParticleTemplate & {
  duration: number;
};

export type ReactionBurstState = {
  emoji: string;
  key: number;
  sourceElement: HTMLElement;
  left: number;
  top: number;
  width: number;
  height: number;
  particles: GiantReactionParticle[];
  phase: "enter" | "exit";
};

export type TriggerReactionBurstOptions = {
  /**
   * When true, climbs to the closest Explore card surface before animating.
   * Stories pass false so the effect covers the story viewer itself.
   */
  resolveSurface?: boolean;
};

const PARTICLE_COUNT = 14;

function randomBetween(min: number, max: number) {
  return min + Math.random() * (max - min);
}

function randomIntBetween(min: number, max: number) {
  return Math.round(randomBetween(min, max));
}

function buildParticlesForCard(): GiantReactionParticle[] {
  return Array.from({ length: PARTICLE_COUNT }, () => {
    const speedFactor = randomBetween(0.78, 1.28);

    return {
      x: randomBetween(10, 90),
      y: randomBetween(16, 86),
      rotation: randomBetween(-18, 18),
      delay: randomIntBetween(0, 520),
      scale: randomBetween(0.48, 0.9),
      speedFactor,
      duration: Math.round(PARTICLE_BASE_DURATION_MS * speedFactor),
    };
  });
}

function getMaxParticleAnimationMs(particles: GiantReactionParticle[]) {
  return particles.reduce(
    (max, particle) => Math.max(max, particle.delay + particle.duration),
    0
  );
}

export function findReactionBurstSurface(el: HTMLElement): HTMLElement {
  const cardSurface = el.closest("[data-card-surface=\"true\"]");
  if (cardSurface instanceof HTMLElement) return cardSurface;

  let current: HTMLElement | null = el.parentElement;

  for (let index = 0; index < 12 && current; index++) {
    const rect = current.getBoundingClientRect();
    const className = typeof current.className === "string" ? current.className : "";
    const looksLikeCard =
      className.includes("overflow-hidden") &&
      className.includes("border") &&
      rect.height > 140 &&
      rect.width > 220 &&
      rect.width < 1000;

    if (looksLikeCard) return current;
    if (rect.height > 150 && rect.width > 220 && rect.width < 1000) return current;

    current = current.parentElement;
  }

  return el;
}

export function useReactionBurst() {
  const [reactionBurst, setReactionBurst] = useState<ReactionBurstState | null>(null);
  const exitTimerRef = useRef<number | null>(null);
  const clearTimerRef = useRef<number | null>(null);

  const clearTimers = useCallback(() => {
    if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    exitTimerRef.current = null;
    clearTimerRef.current = null;
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  useEffect(() => {
    if (!reactionBurst?.sourceElement) return;

    const sourceElement = reactionBurst.sourceElement;

    const syncWithSource = () => {
      if (!sourceElement.isConnected) {
        setReactionBurst(null);
        return;
      }

      const rect = sourceElement.getBoundingClientRect();
      setReactionBurst((prev) => {
        if (!prev) return prev;
        if (
          prev.left === rect.left &&
          prev.top === rect.top &&
          prev.width === rect.width &&
          prev.height === rect.height
        ) {
          return prev;
        }

        return {
          ...prev,
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        };
      });
    };

    window.addEventListener("scroll", syncWithSource, { passive: true });
    window.addEventListener("resize", syncWithSource);

    return () => {
      window.removeEventListener("scroll", syncWithSource);
      window.removeEventListener("resize", syncWithSource);
    };
  }, [reactionBurst?.sourceElement]);

  const triggerReactionBurst = useCallback(
    (
      emoji: string,
      sourceElement?: HTMLElement | null,
      options: TriggerReactionBurstOptions = {}
    ) => {
      if (!sourceElement) return;

      const targetElement =
        options.resolveSurface === false
          ? sourceElement
          : findReactionBurstSurface(sourceElement);
      const targetRect = targetElement.getBoundingClientRect();
      const particles = buildParticlesForCard();
      const key = Date.now();

      clearTimers();
      setReactionBurst({
        emoji,
        key,
        sourceElement: targetElement,
        left: targetRect.left,
        top: targetRect.top,
        width: targetRect.width,
        height: targetRect.height,
        particles,
        phase: "enter",
      });

      exitTimerRef.current = window.setTimeout(() => {
        setReactionBurst((prev) => (prev ? { ...prev, phase: "exit" } : prev));
      }, GIANT_REACTION_HOLD_MS);

      clearTimerRef.current = window.setTimeout(() => {
        setReactionBurst(null);
      }, getMaxParticleAnimationMs(particles) + 300);
    },
    [clearTimers]
  );

  return { reactionBurst, triggerReactionBurst };
}

export function ReactionBurstOverlay({
  burst,
  zIndex = Z_INDEX.EXPLORE_REACTION,
}: {
  burst: ReactionBurstState | null;
  zIndex?: number;
}) {
  if (!burst) return null;

  const emoji = (REACTION_EMOJI as Record<string, string>)[burst.emoji] ?? "👍";

  return createPortal(
    <>
      <style jsx global>{`
        @keyframes reaction-giant-enter {
          0% {
            opacity: 0;
            transform: translateY(8px) scale(0.34) rotate(-2.2deg);
            filter: blur(2.2px);
          }
          26% {
            opacity: 0.82;
            transform: translateY(-6px) scale(1.02) rotate(1.4deg);
            filter: blur(0.7px);
          }
          54% {
            opacity: 1;
            transform: translateY(2px) scale(1.1) rotate(-0.8deg);
            filter: blur(0px);
          }
          78% {
            opacity: 1;
            transform: translateY(-1px) scale(0.985) rotate(0.25deg);
            filter: blur(0px);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1) rotate(0deg);
            filter: blur(0px);
          }
        }

        @keyframes reaction-giant-exit {
          0% {
            opacity: 1;
            transform: translateY(0) scale(1) rotate(0deg);
            filter: blur(0px);
          }
          34% {
            opacity: 0.95;
            transform: translateY(-4px) scale(0.97) rotate(-0.35deg);
            filter: blur(0.2px);
          }
          100% {
            opacity: 0;
            transform: translateY(-22px) scale(0.76) rotate(-1.2deg);
            filter: blur(2px);
          }
        }

        @keyframes reaction-giant-particle {
          0% {
            opacity: 0;
            transform: translate3d(-50%, -50%, 0)
              rotate(var(--rot, 0deg))
              scale(calc(var(--rs, 0.6) * 0.18));
            filter: blur(1px);
          }
          42% {
            opacity: 1;
            transform: translate3d(-50%, -50%, 0)
              rotate(var(--rot, 0deg))
              scale(calc(var(--rs, 0.6) * 1.18));
            filter: blur(0);
          }
          68% {
            opacity: 0.86;
            transform: translate3d(-50%, -50%, 0)
              rotate(var(--rot, 0deg))
              scale(calc(var(--rs, 0.6) * 0.96));
          }
          100% {
            opacity: 0;
            transform: translate3d(-50%, -50%, 0)
              rotate(var(--rot, 0deg))
              scale(calc(var(--rs, 0.6) * 0.72));
            filter: blur(0.7px);
          }
        }

        .reaction-giant-pop-wrap {
          overflow: hidden;
          will-change: transform, opacity, filter;
          backface-visibility: hidden;
          transform-style: preserve-3d;
        }

        .reaction-giant-pop {
          position: absolute;
          inset: 0;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
          will-change: transform, opacity, filter;
          backface-visibility: hidden;
          transform-origin: center;
          transform-style: preserve-3d;
        }

        .reaction-giant-particles {
          position: absolute;
          inset: 0;
          z-index: 1;
          pointer-events: none;
          overflow: hidden;
          -webkit-mask-image: linear-gradient(
            to top,
            transparent 0%,
            rgb(0 0 0 / 0.45) 5%,
            #000 12%,
            #000 90%,
            rgb(0 0 0 / 0.55) 96%,
            transparent 100%
          );
          mask-image: linear-gradient(
            to top,
            transparent 0%,
            rgb(0 0 0 / 0.45) 5%,
            #000 12%,
            #000 90%,
            rgb(0 0 0 / 0.55) 96%,
            transparent 100%
          );
        }

        .reaction-giant-pop--enter {
          animation: reaction-giant-enter 920ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        .reaction-giant-pop--exit {
          animation: reaction-giant-exit ${GIANT_REACTION_EXIT_MS}ms cubic-bezier(0.32, 0, 0.2, 1) both;
        }

        .reaction-giant-particle {
          position: absolute;
          left: var(--px, 50%);
          top: var(--py, 50%);
          line-height: 1;
          opacity: 0;
          will-change: transform, opacity, filter;
          backface-visibility: hidden;
        }

        @media (prefers-reduced-motion: reduce) {
          .reaction-giant-pop--enter {
            animation-duration: 360ms;
          }
          .reaction-giant-pop--exit {
            animation-duration: 300ms;
          }
          .reaction-giant-particle {
            animation-duration: 520ms !important;
            animation-timing-function: ease-out !important;
          }
        }
      `}</style>

      <div
        key={burst.key}
        aria-hidden
        className="reaction-giant-pop-wrap pointer-events-none fixed select-none"
        style={{
          left: burst.left,
          top: burst.top,
          width: burst.width,
          height: burst.height,
          zIndex,
        }}
      >
        <span className="reaction-giant-particles absolute inset-0">
          {burst.particles.map((p, index) => (
            <span
              key={`${burst.key}-${index}`}
              className="reaction-giant-particle text-[36px] sm:text-[48px]"
              style={{
                "--px": `${p.x}%`,
                "--py": `${p.y}%`,
                "--rot": `${p.rotation}deg`,
                "--rs": p.scale,
                animation: `reaction-giant-particle ${p.duration}ms ${PARTICLE_EASE} ${p.delay}ms both`,
              } as React.CSSProperties}
            >
              {emoji}
            </span>
          ))}
        </span>

        <span
          className={cn(
            "reaction-giant-pop text-[76px] leading-none sm:text-[92px]",
            burst.phase === "enter" ? "reaction-giant-pop--enter" : "reaction-giant-pop--exit"
          )}
        >
          {emoji}
        </span>
      </div>
    </>,
    document.body
  );
}
